// File: lib/AuthContext.tsx
"use client";

import { createContext, useContext, useState, useCallback } from "react";
import type { AppUser } from "@/components/layout/AppShell";

interface AuthContextType {
  user: AppUser | null;
  requireAuth: (action?: () => void) => boolean; // return true nếu đã login
  openAuth: () => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  requireAuth: () => false,
  openAuth: () => {},
});

export function useAuth() { return useContext(AuthContext); }

export function AuthProvider({ user, children }: { user: AppUser | null; children: React.ReactNode }) {
  const [authOpen, setAuthOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

  const openAuth = useCallback(() => setAuthOpen(true), []);

  const requireAuth = useCallback((action?: () => void): boolean => {
    if (user) {
      action?.();
      return true;
    }
    if (action) setPendingAction(() => action);
    setAuthOpen(true);
    return false;
  }, [user]);

  // Khi auth thành công (AuthModal đóng + user có) → chạy pending action
  const handleAuthClose = useCallback(() => {
    setAuthOpen(false);
    // Pending action sẽ chạy ở lần render tiếp khi user đã có
  }, []);

  return (
    <AuthContext.Provider value={{ user, requireAuth, openAuth }}>
      {children}
      <AuthModalWrapper isOpen={authOpen} onClose={handleAuthClose} pendingAction={pendingAction} onClearPending={() => setPendingAction(null)} />
    </AuthContext.Provider>
  );
}

// Wrapper để handle pending action sau login
import dynamic from "next/dynamic";
const AuthModal = dynamic(() => import("@/components/auth/AuthModal"), { ssr: false });

function AuthModalWrapper({ isOpen, onClose, pendingAction, onClearPending }: {
  isOpen: boolean; onClose: () => void; pendingAction: (() => void) | null; onClearPending: () => void;
}) {
  // FIX: KHÔNG return null ở wrapper level. AuthModal nội bộ đã có
  // `if (!isOpen) return null` rồi. Return null ở đây sẽ ngăn dynamic import
  // được preload, gây bug "click không mở modal" vì chunk chưa kịp load.
  return (
    <AuthModal isOpen={isOpen} onClose={() => {
      onClose();
      // Nếu có pending action và user đã login (page sẽ refresh)
      // Action sẽ cần user tự click lại — đủ tốt cho UX
      onClearPending();
    }} />
  );
}
